import { Controller, UseFormRegisterReturn, useForm } from "react-hook-form";
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { MemberPayment, MemberPaymentRequest } from "../../../service/model/member-payment.model";
import { Box, Button, Dialog, DialogTitle, FormControl, FormControlLabel, FormHelperText, FormLabel, Grid, Radio, RadioGroup, TextField } from "@mui/material";
import FormField from "../../../components/form-controls/form-field";
import theme from "../../../app/app-theme";
import useMemberships from "../../../hooks/use-memberships";
import { useEffect, useState } from "react";
import { PaymentCategory } from "../../../shared/enums";
import { Setting } from "../../../service/model/setting.model";
import { getBusinessSettings } from "../../../service/settings.service";
import FormCloseButton from "../../../components/form-controls/form-close-button";
import { FormTextField } from "../../../components/form-input-field-wrappers/form-controller-inputs/form-text-field";
import { FormSelectField } from "../../../components/form-input-field-wrappers/form-controller-inputs/form-select-field";

interface MemberPaymentFormProps {
    readonly payment?: MemberPayment
    readonly editable: boolean
    readonly onClose: () => void
    readonly onSubmit: (data: MemberPaymentRequest) => void
}

const style = {
    // position: 'absolute' as 'absolute',
    // top: '50%',
    // left: '50%',
    // transform: 'translate(-50%, -50%)',
    // width: 400,
    // bgcolor: 'background.paper',
    // border: '2px solid #000',
    // boxShadow: 24,
    p: 2,
};

const validationSchema = yup.object().shape({
    amount: yup.number().transform((value) => Number.isNaN(value) ? null : value).required("Amount is required"),
    membershipId: yup.number().transform((value) => Number.isNaN(value) ? null : value ).nullable().when('category', {
        is: PaymentCategory.MEMBERSHIP_FEE,
        then: schema => schema.required("Membership is required"),
        otherwise: schema => schema.notRequired()
    }),
    category: yup.string().required("Category is required"),
    paymentDate: yup.string().required("Payment date is required"),
    fromDate: yup.string().required("From date is required"),
    loginEnabled: yup.boolean().required("Member login option is required").when('category', {
        is: PaymentCategory.MEMBERSHIP_FEE,
        then: schema => schema.required("Member login option is required"),
        otherwise: schema => schema.notRequired()
    }),
});

export type FormValues = yup.InferType<typeof validationSchema>;

const MemberPaymentForm: React.FC<MemberPaymentFormProps> = ({ payment, editable, onClose, onSubmit }) => {
    const [memberships] = useMemberships();
    const [settings, setSettings] = useState<Setting[]>([]);

    const fetchSettings = async () => {
        try {
            const response = await getBusinessSettings();
            setSettings(response);
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    const { register, watch, control, setValue, handleSubmit, formState: { errors } } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            category: payment?.category ?? PaymentCategory.MEMBERSHIP_FEE,
        }
    });

    const selectedMembership = watch("membershipId");
    const selectedCategory = watch("category");
    const selectedAmount = watch("amount");
    const selectedLoginEnabled = watch("loginEnabled");

    useEffect(() => {
        setValue("amount", memberships.find(membership => membership.id === selectedMembership)?.amount ?? payment?.amount ?? 0);
    }, [selectedMembership]);
    
    const onSubmitHandler = (data: any) => {
        const updatedMemberPayment = {
            ...payment,
            ...data
        }
        onSubmit(updatedMemberPayment as MemberPaymentRequest)
    };

    const getRegister = (name: keyof FormValues, validation?: any): UseFormRegisterReturn => {
        return register(name, validation);
    };

    const getTotalAmount = (): number => {
        let baseAmount = 0;
        if(selectedLoginEnabled === true || String(selectedLoginEnabled) === 'true') {
            const membership = memberships.find(m => m.id === selectedMembership);
            if(membership) {
                const monthlyLoginFee = settings.find(s => s.name === 'member_login_fee')?.value ?? 0;
                const loginFee = Math.round(((Number(monthlyLoginFee) * membership.durationDays)/30));
                baseAmount += loginFee;
            }
        }

        return baseAmount + (Number(selectedAmount) || 0);
    }

    return (
        <Dialog open={true} onClose={onClose} sx={style}>
            <DialogTitle>Payment Details</DialogTitle>
            <Box sx={style}>
                <>
                    <FormCloseButton onClose={onClose} />
                    <form onSubmit={handleSubmit(onSubmitHandler)}>
                    <Grid container direction="row" className="mt-0 my-0" justifyContent='space-between'>
                        <FormField>
                            <TextField
                                id="member"
                                label="Member"
                                variant="outlined"
                                defaultValue={`${payment?.firstName} ${payment?.lastName}`}
                                disabled={true}
                            />
                        </FormField>
                        <FormField>
                                <FormSelectField
                                    id={"category"}
                                    label="Category"
                                    name="category"
                                    defaultValue={payment?.category ?? PaymentCategory.MEMBERSHIP_FEE}
                                    errorMessage={errors.category?.message}
                                    disabled={payment?.category != null}
                                    options={
                                        [
                                            {label: 'Membership Fee', value: PaymentCategory.MEMBERSHIP_FEE},
                                            {label: 'Registration Fee', value: PaymentCategory.REGISTRATION_FEE},
                                            {label: 'Login Fee', value: PaymentCategory.LOGIN_FEE},
                                        ]
                                    }
                                    register={register}
                                />
                        </FormField>
                        {selectedCategory === PaymentCategory.MEMBERSHIP_FEE &&
                            <FormField>
                                <FormSelectField
                                    id={"membership"}
                                    label="Membership"
                                    name="membershipId"
                                    defaultValue={payment?.membershipId}
                                    errorMessage={errors.membershipId?.message}
                                    options={memberships.map(membership => { return { label: membership.name, value: membership.id } })}
                                    register={register}
                                />
                            </FormField>
                        }
                        <FormField>
                            <FormTextField
                                id="amount"  
                                label="Amount"
                                name="amount"
                                type="number"
                                defaultValue={payment?.amount}
                                errorMessage={errors.amount?.message}
                                register={register}
                            />
                        </FormField>
                        <FormField>
                            <FormTextField
                                id="paymentDate"
                                label="Payment Date"
                                type="date"
                                name="paymentDate"
                                defaultValue={payment?.paymentDate ?? new Date().toISOString().slice(0, 10)}
                                errorMessage={errors.paymentDate?.message}
                                register={register}
                            />
                        </FormField>
                        <FormField>
                            <FormTextField
                                id="fromDate"
                                label="From Date"
                                type="date"
                                name="fromDate"
                                defaultValue={payment?.fromDate ?? new Date().toISOString().slice(0, 10)}
                                errorMessage={errors.fromDate?.message}
                                register={register}
                            />
                        </FormField>
                        {payment && !payment.id && selectedCategory === PaymentCategory.MEMBERSHIP_FEE && (
                            <FormField>
                                <Controller
                                    name="loginEnabled"
                                    control={control}
                                    rules={{ required: "Member login option is required" }}
                                    render={({ field, fieldState }) => (
                                        <FormControl>
                                            <FormLabel>Enable Member Login</FormLabel>
                                            <RadioGroup {...field} row>
                                                <FormControlLabel value="true" control={<Radio />} onClick={() => setValue("loginEnabled", true)} label="Yes" />
                                                <FormControlLabel value="false" control={<Radio />} onClick={() => setValue("loginEnabled", false)} label="No" />
                                            </RadioGroup>

                                            <FormHelperText sx={{ color: theme.palette.error.main }}>
                                                {fieldState.error
                                                    ? fieldState.error.message
                                                    : ""}
                                            </FormHelperText>
                                        </FormControl>
                                    )}
                                />
                            </FormField>                                        
                        )}
                        <Box sx={{width: '100%'}}>
                        <p>Total Amount: <span>{getTotalAmount()}</span> LKR</p>
                        </Box>
                        <Grid container direction="row" justifyContent='end' sx={{ mt: 1 }}>
                            <Button type="button" variant="contained" sx={{ mr: 1, bgcolor: `${theme.palette.secondary.main}` }} onClick={onClose}>Cancel</Button>
                            <Button type="submit" variant="contained" >Submit</Button>
                        </Grid>
                        </Grid>
                    </form>
                </>
            </Box>
        </Dialog>
    );
}

export default MemberPaymentForm;